// shape-grid.js — Animated grid background with hover fill.
// Auto-initialises every canvas.shape-grid on the page.
// Config is read from data-* attributes on the canvas element.

class ShapeGrid {
  constructor(canvas) {
    this.canvas = canvas;
    this.ctx = canvas.getContext("2d");

    this.direction = canvas.dataset.direction || "right";
    this.speed = Number.isFinite(parseFloat(canvas.dataset.speed))
      ? parseFloat(canvas.dataset.speed)
      : 1;
    this.borderColor =
      canvas.dataset.borderColor || "rgba(255, 255, 255, 0.06)";
    this.squareSize = parseInt(canvas.dataset.squareSize, 10) || 40;
    this.hoverFillColor =
      canvas.dataset.hoverFillColor || "rgba(255, 255, 255, 0.12)";
    this.shape = canvas.dataset.shape || "square";
    this.hoverTrailAmount = parseInt(canvas.dataset.hoverTrailAmount, 10) || 0;
    this.reducedMotion = window.matchMedia(
      "(prefers-reduced-motion: reduce)",
    ).matches;

    this.gridOffset = { x: 0, y: 0 };
    this.hoveredSquare = null;
    this.trailCells = [];
    this.cellOpacities = new Map();
    this.rafId = null;

    this.eventTarget = this._resolveEventTarget();

    this._resize = this._resize.bind(this);
    this._onMove = this._onMove.bind(this);
    this._onLeave = this._onLeave.bind(this);
    this._tick = this._tick.bind(this);

    this._resize();
    window.addEventListener("resize", this._resize);
    this.eventTarget.addEventListener("mousemove", this._onMove);
    this.eventTarget.addEventListener("mouseleave", this._onLeave);
    this.rafId = requestAnimationFrame(this._tick);
  }

  destroy() {
    window.removeEventListener("resize", this._resize);
    this.eventTarget.removeEventListener("mousemove", this._onMove);
    this.eventTarget.removeEventListener("mouseleave", this._onLeave);
    cancelAnimationFrame(this.rafId);
  }

  _resolveEventTarget() {
    const target = this.canvas.dataset.pointerTarget;
    if (target === "section") {
      return this.canvas.closest("section") || this.canvas;
    }
    return this.canvas;
  }

  _resize() {
    const dpr = window.devicePixelRatio || 1;
    const { offsetWidth, offsetHeight } = this.canvas;
    this.canvas.width = Math.round(offsetWidth * dpr);
    this.canvas.height = Math.round(offsetHeight * dpr);
    this.ctx.setTransform(dpr, 0, 0, dpr, 0, 0);
    this.displayWidth = offsetWidth;
    this.displayHeight = offsetHeight;
  }

  _tick() {
    if (!this.reducedMotion) {
      this._updateOffset();
    }
    this._updateOpacities();
    this._draw();
    this.rafId = requestAnimationFrame(this._tick);
  }

  _updateOffset() {
    const { direction, speed, squareSize, shape } = this;

    if (speed <= 0) {
      return;
    }

    const isHex = shape === "hexagon";
    const isTri = shape === "triangle";
    const spd = speed;
    const wrapX = isHex ? squareSize * 1.5 * 2 : squareSize;
    const wrapY = isHex
      ? squareSize * Math.sqrt(3)
      : isTri
        ? squareSize * 2
        : squareSize;

    switch (direction) {
      case "right":
        this.gridOffset.x = (this.gridOffset.x - spd + wrapX) % wrapX;
        break;
      case "left":
        this.gridOffset.x = (this.gridOffset.x + spd + wrapX) % wrapX;
        break;
      case "up":
        this.gridOffset.y = (this.gridOffset.y + spd + wrapY) % wrapY;
        break;
      case "down":
        this.gridOffset.y = (this.gridOffset.y - spd + wrapY) % wrapY;
        break;
      case "diagonal":
        this.gridOffset.x = (this.gridOffset.x - spd + wrapX) % wrapX;
        this.gridOffset.y = (this.gridOffset.y - spd + wrapY) % wrapY;
        break;
      default:
        break;
    }
  }

  _updateOpacities() {
    const targets = new Map();
    if (this.hoveredSquare) {
      targets.set(`${this.hoveredSquare.x},${this.hoveredSquare.y}`, 1);
    }
    if (this.hoverTrailAmount > 0) {
      this.trailCells.forEach((t, i) => {
        const key = `${t.x},${t.y}`;
        if (!targets.has(key)) {
          targets.set(
            key,
            (this.trailCells.length - i) / (this.trailCells.length + 1),
          );
        }
      });
    }
    for (const [key] of targets) {
      if (!this.cellOpacities.has(key)) {
        this.cellOpacities.set(key, 0);
      }
    }
    for (const [key, opacity] of this.cellOpacities) {
      const target = targets.get(key) || 0;
      const next = opacity + (target - opacity) * 0.15;
      if (next < 0.005) {
        this.cellOpacities.delete(key);
      } else {
        this.cellOpacities.set(key, next);
      }
    }
  }

  _draw() {
    const {
      ctx,
      squareSize,
      shape,
      borderColor,
      hoverFillColor,
      gridOffset,
      cellOpacities,
    } = this;
    const width = this.displayWidth;
    const height = this.displayHeight;
    const isHex = shape === "hexagon";
    const isTri = shape === "triangle";
    const hexH = squareSize * 1.5;
    const hexV = squareSize * Math.sqrt(3);

    ctx.clearRect(0, 0, width, height);

    if (isHex) {
      const colShift = Math.floor(gridOffset.x / hexH);
      const offX = ((gridOffset.x % hexH) + hexH) % hexH;
      const offY = ((gridOffset.y % hexV) + hexV) % hexV;
      const cols = Math.ceil(width / hexH) + 3;
      const rows = Math.ceil(height / hexV) + 3;

      for (let col = -2; col < cols; col++) {
        for (let row = -2; row < rows; row++) {
          const cx = col * hexH + offX;
          const cy =
            row * hexV + ((col + colShift) % 2 !== 0 ? hexV / 2 : 0) + offY;
          const key = `${col},${row}`;
          const alpha = cellOpacities.get(key);
          if (alpha) {
            ctx.globalAlpha = alpha;
            this._hexPath(cx, cy, squareSize);
            ctx.fillStyle = hoverFillColor;
            ctx.fill();
            ctx.globalAlpha = 1;
          }
          this._hexPath(cx, cy, squareSize);
          ctx.strokeStyle = borderColor;
          ctx.stroke();
        }
      }
    } else if (isTri) {
      const halfW = squareSize / 2;
      const colShift = Math.floor(gridOffset.x / halfW);
      const rowShift = Math.floor(gridOffset.y / squareSize);
      const offX = ((gridOffset.x % halfW) + halfW) % halfW;
      const offY = ((gridOffset.y % squareSize) + squareSize) % squareSize;
      const cols = Math.ceil(width / halfW) + 4;
      const rows = Math.ceil(height / squareSize) + 4;

      for (let col = -2; col < cols; col++) {
        for (let row = -2; row < rows; row++) {
          const cx = col * halfW + offX;
          const cy = row * squareSize + squareSize / 2 + offY;
          const flip = (((col + colShift + row + rowShift) % 2) + 2) % 2 !== 0;
          const key = `${col},${row}`;
          const alpha = cellOpacities.get(key);
          if (alpha) {
            ctx.globalAlpha = alpha;
            this._triPath(cx, cy, squareSize, flip);
            ctx.fillStyle = hoverFillColor;
            ctx.fill();
            ctx.globalAlpha = 1;
          }
          this._triPath(cx, cy, squareSize, flip);
          ctx.strokeStyle = borderColor;
          ctx.stroke();
        }
      }
    } else if (shape === "circle") {
      const offX = ((gridOffset.x % squareSize) + squareSize) % squareSize;
      const offY = ((gridOffset.y % squareSize) + squareSize) % squareSize;
      const cols = Math.ceil(width / squareSize) + 3;
      const rows = Math.ceil(height / squareSize) + 3;

      for (let col = -2; col < cols; col++) {
        for (let row = -2; row < rows; row++) {
          const cx = col * squareSize + squareSize / 2 + offX;
          const cy = row * squareSize + squareSize / 2 + offY;
          const key = `${col},${row}`;
          const alpha = cellOpacities.get(key);
          if (alpha) {
            ctx.globalAlpha = alpha;
            this._circlePath(cx, cy, squareSize);
            ctx.fillStyle = hoverFillColor;
            ctx.fill();
            ctx.globalAlpha = 1;
          }
          this._circlePath(cx, cy, squareSize);
          ctx.strokeStyle = borderColor;
          ctx.stroke();
        }
      }
    } else {
      const offX = ((gridOffset.x % squareSize) + squareSize) % squareSize;
      const offY = ((gridOffset.y % squareSize) + squareSize) % squareSize;
      const cols = Math.ceil(width / squareSize) + 3;
      const rows = Math.ceil(height / squareSize) + 3;

      for (let col = -2; col < cols; col++) {
        for (let row = -2; row < rows; row++) {
          const sx = col * squareSize + offX;
          const sy = row * squareSize + offY;
          const key = `${col},${row}`;
          const alpha = cellOpacities.get(key);
          if (alpha) {
            ctx.globalAlpha = alpha;
            ctx.fillStyle = hoverFillColor;
            ctx.fillRect(sx, sy, squareSize, squareSize);
            ctx.globalAlpha = 1;
          }
          ctx.strokeStyle = borderColor;
          ctx.strokeRect(sx, sy, squareSize, squareSize);
        }
      }
    }
  }

  _hexPath(cx, cy, size) {
    const { ctx } = this;
    ctx.beginPath();
    for (let i = 0; i < 6; i++) {
      const a = (Math.PI / 3) * i;
      const vx = cx + size * Math.cos(a);
      const vy = cy + size * Math.sin(a);
      if (i === 0) {
        ctx.moveTo(vx, vy);
      } else {
        ctx.lineTo(vx, vy);
      }
    }
    ctx.closePath();
  }

  _circlePath(cx, cy, size) {
    this.ctx.beginPath();
    this.ctx.arc(cx, cy, size / 2, 0, Math.PI * 2);
    this.ctx.closePath();
  }

  _triPath(cx, cy, size, flip) {
    const { ctx } = this;
    ctx.beginPath();
    if (flip) {
      ctx.moveTo(cx, cy + size / 2);
      ctx.lineTo(cx + size / 2, cy - size / 2);
      ctx.lineTo(cx - size / 2, cy - size / 2);
    } else {
      ctx.moveTo(cx, cy - size / 2);
      ctx.lineTo(cx + size / 2, cy + size / 2);
      ctx.lineTo(cx - size / 2, cy + size / 2);
    }
    ctx.closePath();
  }

  _onMove(e) {
    const rect = this.canvas.getBoundingClientRect();
    const mx = e.clientX - rect.left;
    const my = e.clientY - rect.top;
    const { squareSize, shape, gridOffset } = this;
    const isHex = shape === "hexagon";
    const isTri = shape === "triangle";
    let col;
    let row;

    if (isHex) {
      const hexH = squareSize * 1.5;
      const hexV = squareSize * Math.sqrt(3);
      const colShift = Math.floor(gridOffset.x / hexH);
      const offX = ((gridOffset.x % hexH) + hexH) % hexH;
      const offY = ((gridOffset.y % hexV) + hexV) % hexV;
      col = Math.round((mx - offX) / hexH);
      row = Math.round(
        (my - offY - ((col + colShift) % 2 !== 0 ? hexV / 2 : 0)) / hexV,
      );
    } else if (isTri) {
      const halfW = squareSize / 2;
      const offX = ((gridOffset.x % halfW) + halfW) % halfW;
      const offY = ((gridOffset.y % squareSize) + squareSize) % squareSize;
      col = Math.round((mx - offX) / halfW);
      row = Math.floor((my - offY) / squareSize);
    } else if (shape === "circle") {
      const offX = ((gridOffset.x % squareSize) + squareSize) % squareSize;
      const offY = ((gridOffset.y % squareSize) + squareSize) % squareSize;
      col = Math.round((mx - offX) / squareSize);
      row = Math.round((my - offY) / squareSize);
    } else {
      const offX = ((gridOffset.x % squareSize) + squareSize) % squareSize;
      const offY = ((gridOffset.y % squareSize) + squareSize) % squareSize;
      col = Math.floor((mx - offX) / squareSize);
      row = Math.floor((my - offY) / squareSize);
    }

    if (
      !this.hoveredSquare ||
      this.hoveredSquare.x !== col ||
      this.hoveredSquare.y !== row
    ) {
      if (this.hoveredSquare && this.hoverTrailAmount > 0) {
        this.trailCells.unshift({ ...this.hoveredSquare });
        if (this.trailCells.length > this.hoverTrailAmount) {
          this.trailCells.length = this.hoverTrailAmount;
        }
      }
      this.hoveredSquare = { x: col, y: row };
    }
  }

  _onLeave() {
    if (this.hoveredSquare && this.hoverTrailAmount > 0) {
      this.trailCells.unshift({ ...this.hoveredSquare });
      if (this.trailCells.length > this.hoverTrailAmount) {
        this.trailCells.length = this.hoverTrailAmount;
      }
    }
    this.hoveredSquare = null;
  }
}

function initShapeGrids() {
  document.querySelectorAll("canvas.shape-grid").forEach((el) => {
    new ShapeGrid(el);
  });
}

if (document.readyState === "loading") {
  document.addEventListener("DOMContentLoaded", initShapeGrids);
} else {
  initShapeGrids();
}

export default ShapeGrid;
